import { faRefresh } from "@fortawesome/pro-regular-svg-icons";
import { type PropsWithChildren } from "react";

import { useGetMenus } from "~/api/menus/hooks/use-get-menus";
import { T } from "@repo/transifex";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { ErrorDisplay } from "~/components/collect/error-display";
import { useUser } from "~/api/users/hooks/use-user";
import { useCreatePreOrders } from "~/api/orders/hooks/use-create-pre-orders";

import { CollectBasketLayout } from "../__app.collect.orders/basket/layout";
import { OrdersList } from "../__app.collect.orders/basket/orders-list";
import { useCollectPreOrderLines } from "../__app.collect.orders.create/orders-create-context";
import { useCollectDebtGate } from "../__app.collect.orders.create/use-collect-debt-gate";

import { MenuList } from "./menu-list";
import { OrdersTotalPrice } from "./orders-total-price";
import { HeaderButtons } from "./header-buttons";
import { NegativeBalanceWarning } from "./negative-balance-warning";
import { parsePreOrderLines } from "./parse-pre-order-lines";
import { sumPreOrderLines } from "./pre-order-totals";

type OrdersCreateContainerProps = {
  childId: string;
  groupName: string;
};

export const OrdersCreateContainer = ({
  childId,
  groupName
}: OrdersCreateContainerProps) => {
  const { locale, currency } = useCurrentTillLocalisation();

  const { data, status, refetch, isRefetching } = useGetMenus({ childId });
  const { status: userStatus } = useUser({ childId });

  const { mutate: createOrders, status: createOrdersStatus } =
    useCreatePreOrders();

  const preOrderLines = useCollectPreOrderLines();

  const { shortfallInclVat, resultingBalanceInclVat, takeCandidate } =
    useCollectDebtGate({ childId });

  if (status === "pending" || userStatus === "pending") {
    return (
      <CreateLayout>
        <OrdersList isLoading />
      </CreateLayout>
    );
  }

  if (status === "error" || userStatus === "error") {
    return (
      <CreateLayout>
        <ErrorDisplay>
          <ErrorDisplay.Title>
            <T _str="Something went wrong!" />
          </ErrorDisplay.Title>
          <ErrorDisplay.Description>
            <T _str="We couldn’t load the user's menu" />
          </ErrorDisplay.Description>
          <ErrorDisplay.Action
            variant="danger"
            onClick={() => refetch()}
            loading={isRefetching}
            icon={faRefresh}
          >
            <T _str="Try again" />
          </ErrorDisplay.Action>
        </ErrorDisplay>
      </CreateLayout>
    );
  }

  const { totalInclVat, depositTotalInclVat } = sumPreOrderLines(preOrderLines);

  const isEmptyBasket = preOrderLines.length === 0;

  const isBalanceInsufficient = shortfallInclVat !== null;

  const hasNegativeResultingBalance =
    !isBalanceInsufficient &&
    resultingBalanceInclVat !== null &&
    resultingBalanceInclVat < 0;

  const handleCreateOrders = () => {
    const basket = parsePreOrderLines(data?.menus || [], preOrderLines);

    createOrders({
      childId,
      groupName,
      basket
    });
  };

  return (
    <CreateLayout
      header={
        isEmptyBasket ? null : (
          <OrdersTotalPrice
            currency={currency}
            unitPriceTotalInclVat={totalInclVat}
            depositTotalInclVat={depositTotalInclVat}
            locale={locale}
            isHighlighted={isBalanceInsufficient}
          />
        )
      }
      banner={hasNegativeResultingBalance ? <NegativeBalanceWarning /> : null}
      isSaveDisabled={
        createOrdersStatus === "pending" ||
        isEmptyBasket ||
        isBalanceInsufficient
      }
      isBalanceInsufficient={isBalanceInsufficient}
      onSave={handleCreateOrders}
    >
      {data.menus.map((menu) => (
        <MenuList
          key={menu.moduleId}
          menu={menu}
          currency={currency}
          childId={childId}
          takeCandidate={takeCandidate}
        />
      ))}
    </CreateLayout>
  );
};

type CreateLayoutProps = PropsWithChildren<{
  header?: React.ReactNode;
  banner?: React.ReactNode;
  isBalanceInsufficient?: boolean;
  onSave?: () => void;
  isSaveDisabled?: boolean;
}>;

const CreateLayout = ({
  children,
  header,
  banner,
  isBalanceInsufficient,
  isSaveDisabled,
  onSave
}: CreateLayoutProps) => {
  return (
    <CollectBasketLayout
      tabs={
        <HeaderButtons
          isBalanceInsufficient={isBalanceInsufficient ?? false}
          isSaveDisabled={isSaveDisabled ?? true}
          onSave={onSave}
        />
      }
      title={<T _str="Create Order" />}
      header={header}
      banner={banner}
    >
      {children}
    </CollectBasketLayout>
  );
};
